/**
 * What gets recorded, and how much of it.
 *
 * The previous interceptor logged every request — including GETs — with the
 * full response payload, then masked that whole tree synchronously on the event
 * loop before writing. The cost therefore scaled with response size, which is
 * exactly backwards: a 100 KB product listing was the most expensive thing to
 * record and the least useful to keep.
 */

/** Paths that are never recorded, whatever the method or status. */
const EXCLUDED_PREFIXES = [
  '/api/health',
  '/api/uploads',
  '/api/docs',
  '/api/swagger',
  '/api/activity', // the activity endpoint would otherwise log itself
  '/favicon.ico',
];

/** Stored JSON is truncated here. A row is a pointer for an investigation, not a backup of the body. */
export const PAYLOAD_CAP_BYTES = 8 * 1024;

/**
 * Stored request headers. An allowlist rather than "store everything and redact
 * by pattern" — it is smaller, it skips the masking walk entirely, and it fails
 * safe when some new sensitive header appears.
 */
export const HEADER_ALLOWLIST = [
  'user-agent',
  'referer',
  'origin',
  'content-type',
  'content-length',
  'accept-language',
  'x-store-id',
  'x-correlation-id',
  'x-forwarded-for',
];

export interface SamplingDecision {
  record: boolean;
  /** 1 = recorded every time; N = one in N. Stored so counts drawn from the table are honest. */
  sampleRate: number;
  captureRequestBody: boolean;
  captureResponseBody: boolean;
}

const SKIP: SamplingDecision = {
  record: false,
  sampleRate: 0,
  captureRequestBody: false,
  captureResponseBody: false,
};

export function successfulGetSampleRate(): number {
  const raw = Number(process.env.AUDIT_GET_SAMPLE_RATE ?? 100);
  return Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 100;
}

export function isExcludedPath(path: string): boolean {
  const clean = path.split('?')[0];
  return EXCLUDED_PREFIXES.some((prefix) => clean.startsWith(prefix));
}

/**
 * Decides before any payload is touched — the filter has to come first, or the
 * work it is meant to avoid has already happened.
 */
export function decideSampling(
  method: string,
  path: string,
  statusCode: number,
  random: () => number = Math.random
): SamplingDecision {
  if (isExcludedPath(path)) return SKIP;
  if (method === 'OPTIONS' || method === 'HEAD') return SKIP;

  // Server errors: always, with the response body. This is the one case where
  // the body is worth its storage, because it is the case someone will need to
  // read afterwards.
  if (statusCode >= 500) {
    return {
      record: true,
      sampleRate: 1,
      captureRequestBody: true,
      captureResponseBody: true,
    };
  }

  // Client errors: always, with the request that caused them — but not the
  // response, which is just our own error envelope.
  if (statusCode >= 400) {
    return {
      record: true,
      sampleRate: 1,
      captureRequestBody: true,
      captureResponseBody: false,
    };
  }

  // Every mutation, always. These are the rows an audit trail exists for.
  if (method !== 'GET') {
    return {
      record: true,
      sampleRate: 1,
      captureRequestBody: true,
      captureResponseBody: false,
    };
  }

  // Successful reads: sampled, metadata only.
  const rate = successfulGetSampleRate();
  if (rate <= 1) {
    return { record: true, sampleRate: 1, captureRequestBody: false, captureResponseBody: false };
  }

  return random() * rate < 1
    ? { record: true, sampleRate: rate, captureRequestBody: false, captureResponseBody: false }
    : SKIP;
}

/**
 * Serialises with a hard size ceiling, so the cost of a row is bounded by the
 * cap rather than by how large the payload happened to be.
 */
export function capPayload(
  value: unknown,
  cap = PAYLOAD_CAP_BYTES
): { value: unknown; truncated: boolean } {
  if (value === undefined || value === null) return { value: undefined, truncated: false };

  let serialised: string;
  try {
    serialised = JSON.stringify(value);
  } catch {
    return { value: { __unserialisable: true }, truncated: true };
  }

  if (serialised === undefined) return { value: undefined, truncated: false };
  if (Buffer.byteLength(serialised, 'utf8') <= cap) return { value, truncated: false };

  return {
    value: {
      __truncated: true,
      __bytes: Buffer.byteLength(serialised, 'utf8'),
      preview: serialised.slice(0, cap),
    },
    truncated: true,
  };
}

/** Keeps only allowlisted headers, lowercased. */
export function pickHeaders(headers: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!headers) return undefined;

  const picked: Record<string, unknown> = {};
  for (const key of HEADER_ALLOWLIST) {
    const value = headers[key] ?? headers[key.toLowerCase()];
    if (value !== undefined) picked[key] = value;
  }

  return Object.keys(picked).length ? picked : undefined;
}

/**
 * Collapses concrete ids out of a path so aggregate queries group correctly
 * instead of splitting into one bucket per record.
 */
export function routePattern(path: string): string {
  return path
    .split('?')[0]
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return ':id';
      if (/^\d+$/.test(segment)) return ':id';
      if (/^[0-9a-f]{24,}$/i.test(segment)) return ':id';
      return segment;
    })
    .join('/');
}
